import { useEffect, useState } from "react";
import { Layout } from "../components/layout.tsx";
import { useAuth } from "../context/auth.tsx";
import { listContent, transitionContent, type MarketingContent } from "../api/marketing.ts";

const STATUS_COLOR: Record<string, string> = {
  DRAFT: "bg-gray-700/40 text-gray-300",
  GENERATING: "bg-yellow-900/40 text-yellow-300",
  REVIEW: "bg-blue-900/40 text-blue-300",
  APPROVED: "bg-green-900/40 text-green-300",
  PUBLISHED: "bg-purple-900/40 text-purple-300",
  ARCHIVED: "bg-gray-800/40 text-gray-500",
};

export function MarketingContentPage() {
  const { token } = useAuth();
  const [items, setItems] = useState<MarketingContent[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [transitioning, setTransitioning] = useState<string | null>(null);

  const load = () => {
    if (token == null) return;
    setLoading(true);
    listContent(token, { type: typeFilter || undefined, status: statusFilter || undefined, limit: 50 })
      .then((r) => { setItems(r.items); setTotal(r.total); })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  };

  useEffect(load, [token, typeFilter, statusFilter]);

  async function handleTransition(item: MarketingContent, status: string) {
    if (token == null) return;
    setTransitioning(item.id);
    try {
      await transitionContent(token, item.id, status, item.version);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Transition failed");
    } finally {
      setTransitioning(null);
    }
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Content ({total.toLocaleString()})</h1>
          <div className="flex gap-2">
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
              className="text-sm bg-gray-900 border border-gray-700 rounded px-2 py-1 text-gray-300">
              <option value="">All Types</option>
              {["blog", "linkedin", "twitter", "facebook", "instagram", "email", "case_study", "service_page", "landing_page"].map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              className="text-sm bg-gray-900 border border-gray-700 rounded px-2 py-1 text-gray-300">
              <option value="">All Statuses</option>
              {["DRAFT", "GENERATING", "REVIEW", "APPROVED", "PUBLISHED", "ARCHIVED"].map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
        </div>

        {error != null && <p className="text-red-400 text-sm">{error}</p>}
        {loading && <p className="text-gray-400 text-sm">Loading…</p>}

        <div className="space-y-3">
          {items.map((item) => (
            <div key={item.id} className="rounded-lg border border-gray-800 p-4 space-y-2">
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1 flex-1 min-w-0">
                  <p className="font-medium text-white truncate">{item.title}</p>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span>{item.type}</span>
                    <span>·</span>
                    <span>{new Date(item.createdAt).toLocaleDateString()}</span>
                    {item.generatedBy != null && <><span>·</span><span>AI: {item.generatedBy}</span></>}
                  </div>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded shrink-0 ${STATUS_COLOR[item.status] ?? "bg-gray-700 text-gray-400"}`}>{item.status}</span>
              </div>

              <div className="flex gap-2 flex-wrap">
                {item.status === "REVIEW" && (
                  <button
                    disabled={transitioning === item.id}
                    onClick={() => void handleTransition(item, "APPROVED")}
                    className="text-xs px-3 py-1 rounded bg-green-900/50 text-green-300 hover:bg-green-800/50 disabled:opacity-50"
                  >
                    Approve
                  </button>
                )}
                {item.status === "APPROVED" && (
                  <button
                    disabled={transitioning === item.id}
                    onClick={() => void handleTransition(item, "PUBLISHED")}
                    className="text-xs px-3 py-1 rounded bg-purple-900/50 text-purple-300 hover:bg-purple-800/50 disabled:opacity-50"
                  >
                    Publish
                  </button>
                )}
                {(item.status === "DRAFT" || item.status === "REVIEW" || item.status === "APPROVED") && (
                  <button
                    disabled={transitioning === item.id}
                    onClick={() => void handleTransition(item, "ARCHIVED")}
                    className="text-xs px-3 py-1 rounded bg-gray-800 text-gray-400 hover:bg-gray-700 disabled:opacity-50"
                  >
                    Archive
                  </button>
                )}
              </div>
            </div>
          ))}
          {items.length === 0 && !loading && (
            <p className="text-center text-gray-500 py-8">No content found.</p>
          )}
        </div>
      </div>
    </Layout>
  );
}
